import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Activity className="w-6 h-6 text-black" />
          </div>
          <span className="font-secondary font-bold text-2xl tracking-tight">Revivo<span className="text-primary">&nbsp;IQ</span></span>
        </Link>

        {/* Login Card */}
        <div className="hpme-card p-8">
          <div className="hpme-card-corner" />
          
          <h1 className="text-3xl font-secondary font-bold tracking-tight mb-2">WELCOME BACK</h1>
          <p className="text-zinc-400 mb-8">Sign in to access your performance dashboard</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-sm mb-6" data-testid="login-error">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="hpme-label mb-2 block">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hpme-input w-full"
                placeholder="athlete@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <Label className="hpme-label mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="hpme-input w-full pr-10"
                  placeholder="Enter your password"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="hpme-btn-primary w-full" 
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline" data-testid="login-register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const Register = () => {
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'athlete';
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(email, password, name, role);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
            <Activity className="w-6 h-6 text-black" />
          </div>
          <span className="font-secondary font-bold text-2xl tracking-tight">Revivo<span className="text-primary">&nbsp;IQ</span></span>
        </Link>

        {/* Register Card */}
        <div className="hpme-card p-8">
          <div className="hpme-card-corner" />
          
          <h1 className="text-3xl font-secondary font-bold tracking-tight mb-2">CREATE ACCOUNT</h1>
          <p className="text-zinc-400 mb-8">Start your performance journey today</p>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-sm mb-6" data-testid="register-error">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="hpme-label mb-2 block">Full Name</Label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="hpme-input w-full"
                placeholder="John Smith"
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <Label className="hpme-label mb-2 block">Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hpme-input w-full"
                placeholder="athlete@example.com"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label className="hpme-label mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="hpme-input w-full pr-10"
                  placeholder="Create a password"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="hpme-label mb-3 block">I am a</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setRole('athlete')}
                  className={`p-4 border rounded-sm transition-colors ${
                    role === 'athlete' 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid="register-role-athlete"
                >
                  <p className="font-secondary font-bold">ATHLETE</p>
                  <p className="text-xs text-zinc-400 mt-1">Train & track progress</p>
                </button>
                <button
                  type="button"
                  onClick={() => setRole('coach')}
                  className={`p-4 border rounded-sm transition-colors ${
                    role === 'coach' 
                      ? 'border-primary bg-primary/10 text-primary' 
                      : 'border-zinc-800 hover:border-zinc-700'
                  }`}
                  data-testid="register-role-coach"
                >
                  <p className="font-secondary font-bold">COACH</p>
                  <p className="text-xs text-zinc-400 mt-1">Create workout plans</p>
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="hpme-btn-primary w-full" 
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="register-login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Login, Register };
